import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Award, Download, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { downloadBlob, errMsg } from "../lib/api";

/**
 * Phase 124 — Ownership Transfer Certificate helpers shared by the staff, citizen and public screens.
 *
 * The certificate PDF is rendered server-side; the browser origin is passed along so the verification
 * QR / link printed on it points at this deployment when no PUBLIC_BASE_URL is configured.
 */
export const originParam = () => `origin=${encodeURIComponent(window.location.origin)}`;

export const certificateVerifyPath = (code, token) => (token ? `/${code}/certificate/${token}` : null);

/** Staff download — `/t/{code}/transfer-cases/{id}/certificate` (admin / supervisor / initiating collector). */
export function CertificateDownloadButton({ code, caseRef, certificateNumber, size = "sm", variant = "outline", className = "", testId, label = "Certificate" }) {
  const [busy, setBusy] = useState(false);
  const download = async (e) => {
    e?.stopPropagation?.();
    setBusy(true);
    try {
      await downloadBlob(`/t/${code}/transfer-cases/${caseRef}/certificate?${originParam()}`,
        `transfer-certificate-${certificateNumber || caseRef}.pdf`);
      toast.success("Transfer certificate downloaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size={size} variant={variant} onClick={download} disabled={busy} className={className} data-testid={testId || `certificate-download-${caseRef}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />} {label}
    </Button>
  );
}

/** Public (tokenized) download — used by citizens and the verification page; no login required. */
export function PublicCertificateDownloadButton({ code, token, certificateNumber, size = "sm", variant = "default", className = "", testId, label = "Download PDF" }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await downloadBlob(`/t/${code}/public/transfer-certificate/${token}/pdf?${originParam()}`,
        `transfer-certificate-${certificateNumber || token}.pdf`);
      toast.success("Transfer certificate downloaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size={size} variant={variant} onClick={download} disabled={busy || !token} className={className} data-testid={testId || `public-certificate-download-${certificateNumber || token}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />} {label}
    </Button>
  );
}

/** "Verify" link to the public certificate page (only when a token exists). */
export function CertificateVerifyLink({ code, token, size = "sm", testId, className = "" }) {
  const path = certificateVerifyPath(code, token);
  if (!path) return null;
  return (
    <Button size={size} variant="ghost" asChild className={className} data-testid={testId || `certificate-verify-${token}`}>
      <Link to={path} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Verify page</Link>
    </Button>
  );
}

/** Inline chip with the certificate number. */
export function CertificateChip({ certificateNumber, testId }) {
  if (!certificateNumber) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-mono text-primary" data-testid={testId || `certificate-chip-${certificateNumber}`}>
      <Award className="h-3 w-3" /> {certificateNumber}
    </span>
  );
}
